import { useState, useEffect, useRef } from 'react';
import { callAPI, s3CacheKey, userErrorMessage, showErrorToast } from 'helpers';
import { normalizeDraftPriorities } from './priorities';

// Hooks
import { useDispatch } from 'react-redux';

// จัดอันดับ draft ใหม่ให้ต่อเนื่อง 1..N จากแถวที่มี priority เท่านั้น — แถวที่ไม่ติดอันดับคง null
// (ห้าม normalize ทั้ง array ไม่งั้นสาขาที่แค่สนใจโดนจัดอันดับไปด้วย)
const compactDraft = (favorites) => {
  const selected = favorites.filter(data => data.priority != null).sort((a, b) => a.priority - b.priority);
  const priorities = new Map(normalizeDraftPriorities(selected).map(data => [data._id, data.priority]));
  return favorites.map(data => ({ ...data, priority: priorities.get(data._id) ?? null }));
};

// _priority = อันดับที่ server บันทึกไว้ (priority = draft ที่กำลังแก้) — Round3 ใช้ตัดสินว่าเด็ก
// "มีอันดับอยู่" แม้ draft จะถอดออกหมดแล้ว จะได้ยังเซฟรายการว่างล้างอันดับใน server ได้
const withSavedPriority = (favorites) => favorites.map(data => ({ ...data, _priority: data.priority }));

const useFavorites = () => {
  const dispatch = useDispatch();
  // G113-1: คำตอบหน้าเก่าห้ามทับ Redux/draft หรือแจ้งผลบนหน้าที่เปลี่ยนไปแล้ว
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => { setLoading(true); setLoadError(false); setReloadKey(key => key + 1); };
  const [favorites, setFavorites] = useState([]);
  const [favoritesRevision, setRevisionState] = useState();
  const revisionRef = useRef();
  const mutationRef = useRef(false);
  const [mutationBusy, setMutationBusy] = useState(false);
  const setFavoritesRevision = revision => {
    revisionRef.current = revision;
    setRevisionState(revision);
  };
  // One shared, synchronous guard for add/delete/save, including clicks before React rerenders.
  const mutate = async operation => {
    if (!mountedRef.current) return;
    if (mutationRef.current) {
      showErrorToast('กำลังบันทึกรายการ กรุณารอสักครู่แล้วลองอีกครั้ง');
      return;
    }
    mutationRef.current = true;
    setMutationBusy(true);
    try {
      const result = await operation();
      if (mountedRef.current) return result;
    } catch (error) {
      if (mountedRef.current) throw error;
    }
    finally {
      mutationRef.current = false;
      if (mountedRef.current) setMutationBusy(false);
    }
  };
  const acceptSavedFavorites = ({ favorites: saved, favoritesRevision: revision }) => {
    if (!mountedRef.current) return;
    setFavorites(withSavedPriority(saved));
    setFavoritesRevision(revision);
    dispatch({ type: 'CLEAR_ADMISSION_REMOVES' });
  };
  const [studentStatus, setStudentStatus] = useState();
  const [isApplicable, setApplicable] = useState(false);
  const [redeemableAmount, setRedeemableAmount] = useState(0);
  // เงินอุดหนุนที่ใช้ไปแล้วจริง (server) — null = API เก่า ใช้สูตรเดิม 600 - balance
  const [redeemedAmount, setRedeemedAmount] = useState(null);
  const [alreadySetZero, setAlreadySetZero] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      try {
        const [courses, admissionCountMap, { favorites, favoritesRevision, student_status, applicable }, { already_set_zero, balance, redeemed_amount }] = await Promise.all([
          callAPI({ fullURL: `https://my-tcas.s3.ap-southeast-1.amazonaws.com/mytcas/courses_round3.json?timestamp=${s3CacheKey()}` }),
          callAPI({ fullURL: `https://my-tcas.s3.ap-southeast-1.amazonaws.com/mytcas/count_round3.json?timestamp=${s3CacheKey()}` }),
          callAPI({ url: '/applicants/me/favorites' }),
          callAPI({ url: '/applicants/me/r3-redeemable' })
        ])
        if (!active) return;
        dispatch({ type: 'UPDATE_COURSES', courses });
        dispatch({ type: 'UPDATE_ADMISSION_COUNT_MAP', admissionCountMap });
        setFavorites(withSavedPriority(favorites));
        setFavoritesRevision(favoritesRevision);
        dispatch({ type: 'CLEAR_ADMISSION_REMOVES' });
        setStudentStatus(student_status);
        setApplicable(applicable);
        setRedeemableAmount(balance);
        setRedeemedAmount(typeof redeemed_amount === 'number' ? redeemed_amount : null);
        setAlreadySetZero(already_set_zero);
        setLoading(false);
      } catch (error) {
        if (!active) return;
        console.log({ error });
        setLoadError(true);
        setLoading(false);
      }

    }

    fetchData();
    return () => { active = false; };
  }, [dispatch, reloadKey]);

  const [adding, setAdding] = useState({});
  const onAdd = (programProjectId) => mutate(async () => {
    if (adding[programProjectId]) return;
    try {
      setAdding(_map => ({ ..._map, [programProjectId]: true }));
      const { result, favoritesRevision: revision } = await callAPI({
        method: 'POST',
        url: '/applicants/me/favorites',
        body: { programProjectId, expectedRevision: revisionRef.current }
      });
      if (!mountedRef.current) return;
      setFavorites(_favorites => _favorites.concat([result]));
      setFavoritesRevision(revision);
      setAdding(_map => ({ ..._map, [programProjectId]: false }));
    } catch (error) {
      if (!mountedRef.current) return;
      console.log({ error });
      showErrorToast(userErrorMessage(error));
      setAdding(_map => ({ ..._map, [programProjectId]: false }));
    }
  });

  const [removing, setRemoving] = useState({});
  const onRemove = (favoriteId) => mutate(async () => {
    if (removing[favoriteId]) return;
    try {
      setRemoving(_map => ({ ..._map, [favoriteId]: true }));
      const saved = await callAPI({
        method: 'DELETE',
        url: `/applicants/me/favorites/${favoriteId}`,
        body: { expectedRevision: revisionRef.current }
      });
      if (!mountedRef.current) return;
      // เก็บ draft ที่ยังไม่บันทึกไว้ (reorder/ถอดอันดับ) — แค่ตัดตัวที่ลบแล้วบีบอันดับที่เหลือ
      // ไม่เอา favorites จาก server มาทับ; รับแค่ revision ใหม่
      setFavorites(_favorites => compactDraft(_favorites.filter(({ _id }) => _id !== favoriteId)));
      setFavoritesRevision(saved.favoritesRevision);
      setRemoving(_map => ({ ..._map, [favoriteId]: false }));
    } catch (error) {
      if (!mountedRef.current) return;
      console.log({ error });
      showErrorToast(userErrorMessage(error));
      setRemoving(_map => ({ ..._map, [favoriteId]: false }));
    }
  });

  const onSavePriorities = priorities => mutate(async () => {
    const saved = await callAPI({
      method: 'POST',
      url: '/applicants/me/favorites/reprioritize',
      body: { priorities, expectedRevision: revisionRef.current }
    });
    acceptSavedFavorites(saved);
    return saved;
  });

  const onSelect = (favoriteId) => {
    if (mutationRef.current) return;
    setFavorites(_favorites => {
      const added = _favorites.find(data => data._id === favoriteId);
      const selected = _favorites.filter(data => data.priority != null);
      // ติดอันดับอยู่แล้ว = ไม่ทำอะไร (กัน modal ที่จำ id ค้างมาย้ายอันดับเดิมไปต่อท้ายเงียบ ๆ)
      if (!added || added.priority != null || selected.length >= 10) return _favorites;
      const nextPriority = (Math.max(0, ...selected.map(({ priority }) => priority))) + 1;
      return compactDraft(_favorites.map(data => data._id === favoriteId ? { ...data, priority: nextPriority } : data));
    });
    dispatch({ type: 'ADD_ADMISSION', favoriteId });
  }

  const onDragEnd = (admissions, result, courseMap) => {
    if (mutationRef.current) return;
    // dropped outside the list
    if (!result.destination) return;

    const [removed] = admissions.splice(result.source.index, 1);
    admissions.splice(result.destination.index, 0, removed);
    const priorityMap = admissions.reduce((map, { _id }, index) => ({ ...map, [_id]: index + 1 }), {});

    // const { draggable, project } = admissions.reduce(({ draggable, project }, { _id, program_project }) => {
    //   const _course = courseMap[program_project];
    //   return {
    //     draggable: draggable && (!_course.min_priority || _course.min_priority >= priorityMap[_id]),
    //     project: _course || (_course.min_priority && _course.min_priority < priorityMap[_id] ? _course : null)
    //   }
    // }, { draggable: true, project: null });

    // if (draggable) {
    setFavorites(_favorites => _favorites.map(data =>
      priorityMap[data._id]
        ? { ...data, priority: priorityMap[data._id] }
        : { ...data, priority: null }
    ));
    // } else {
    //   alert(`ไม่สามารถสลับลำดับได้ ลำดับขั้นต่ำในการเลือกสาขาของ ${project.program_name_th} ${project.university_name_th} คือ ${project.min_priority}`);
    // }
  }

  const onRemovePriority = (removed) => {
    if (mutationRef.current) return;
    setFavorites(_favorites => compactDraft(_favorites.map(data => data._id === removed._id ? { ...data, priority: null } : data)));
    dispatch({ type: 'REMOVE_ADMISSION', favoriteId: removed._id });
  }

  return {
    loading,
    loadError,
    reload,
    favorites,
    favoritesRevision,
    acceptSavedFavorites,
    mutationBusy,
    onSavePriorities,
    studentStatus,
    isApplicable,

    adding,
    onAdd,

    removing,
    onRemove,

    onSelect,
    onDragEnd,
    onRemovePriority,

    alreadySetZero,
    redeemableAmount,
    redeemedAmount,
  }
}

export default useFavorites;
